import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/Card';
import { Nav } from '../components/Nav';
import { NotificationPreferencesCard } from '../components/NotificationPreferencesCard';
import { PreferencesCard } from '../components/PreferencesCard';
import { QuietHoursCard } from '../components/QuietHoursCard';
import { RemindersCard } from '../components/RemindersCard';
import { APIError, api } from '../lib/api';
import type { UserSettings } from '../lib/api';
import { useAuth } from '../lib/auth';

// Settings — Account info, password change, notification preferences (built-in
// reminders), custom user reminders, quiet hours, and global preferences.
// Sits outside the bottom Nav; reachable from Dashboard's footer.
export default function Settings() {
  const { state, signOut } = useAuth();
  const navigate = useNavigate();
  const user = state.status === 'authed' ? state.user : null;
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [settingsErr, setSettingsErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getUserSettings()
      .then((s) => {
        if (!cancelled) setSettings(s);
      })
      .catch((e) => {
        if (cancelled) return;
        setSettingsErr(e instanceof APIError ? `Settings: ${e.status}` : 'Settings load failed');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <>
      <div className="min-h-full max-w-md lg:max-w-4xl mx-auto px-5 lg:px-8 pt-8 lg:pt-12 pb-32 space-y-5 stack-2col">
        <header className="space-y-2">
          <p className="label-eyebrow">Account</p>
          <h1 className="font-display text-5xl leading-none">Settings</h1>
        </header>

        <Card title="Profile" hint="Your account info. Edits aren't available yet.">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-fg-muted">Name</dt>
            <dd className="text-right truncate">{user?.name ?? '—'}</dd>
            <dt className="text-fg-muted">Email</dt>
            <dd className="text-right truncate">{user?.email ?? '—'}</dd>
          </dl>
        </Card>

        <NotificationPreferencesCard />
        <RemindersCard />
        {settingsErr && <p className="text-xs text-danger">{settingsErr}</p>}
        {settings && <QuietHoursCard settings={settings} onChange={setSettings} />}
        {settings && <PreferencesCard settings={settings} onChange={setSettings} />}

        <ChangePasswordCard />

        <Card title="Sign out" hint="You'll be sent back to the sign-in screen.">
          <button
            type="button"
            onClick={handleSignOut}
            className="btn-secondary w-full px-4 py-3 font-mono text-xs uppercase tracking-[0.16em] text-danger! border-danger/30! hover:bg-danger-soft!"
          >
            Sign out
          </button>
        </Card>
      </div>
      <Nav />
    </>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Clear stale status messages when the user starts editing — otherwise
  // "Password updated" stays visible next to a half-typed new password.
  const onEdit = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    if (success) setSuccess(false);
    if (error) setError(null);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    // Client-side mirrors of the server checks. Catches typos before
    // burning a 400 round-trip and gives field-specific messaging.
    if (next.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (next !== confirm) {
      setError("New passwords don't match");
      return;
    }
    if (next === current) {
      setError('New password must differ from current');
      return;
    }
    setBusy(true);
    try {
      await api.changePassword({ current_password: current, new_password: next });
      setSuccess(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      if (err instanceof APIError) {
        if (err.status === 401) {
          setError('Current password is incorrect');
        } else {
          const body = err.body as { error?: string } | null;
          setError(body?.error ?? `error ${err.status}`);
        }
      } else {
        setError(String(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Change password" hint="Min 8 characters. Other devices will be signed out.">
      <form onSubmit={submit} className="space-y-3">
        <Field
          label="Current password"
          type="password"
          value={current}
          onChange={onEdit(setCurrent)}
          autoComplete="current-password"
          required
        />
        <Field
          label="New password"
          type="password"
          value={next}
          onChange={onEdit(setNext)}
          autoComplete="new-password"
          placeholder="min 8 characters"
          required
        />
        <Field
          label="Confirm new password"
          type="password"
          value={confirm}
          onChange={onEdit(setConfirm)}
          autoComplete="new-password"
          required
        />
        <button
          type="submit"
          disabled={busy || !current || !next || !confirm}
          className="btn-primary w-full py-3.5 font-mono uppercase tracking-[0.14em] text-sm"
        >
          {busy ? 'Saving…' : 'Update password →'}
        </button>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        {success && (
          <p role="status" className="text-sm text-good">
            Password updated. Other devices have been signed out.
          </p>
        )}
      </form>
    </Card>
  );
}

type FieldProps = {
  label: string;
  value: string;
  onChange: (v: string) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'>;

function Field({ label, value, onChange, ...rest }: FieldProps) {
  return (
    <label className="block space-y-1.5">
      <span className="label-eyebrow">{label}</span>
      <input
        {...rest}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-control bg-bg-sunken border border-line px-3 py-3 text-base text-fg placeholder:text-fg-faint transition-colors focus:outline-none focus:border-accent focus:bg-bg-raised"
      />
    </label>
  );
}
